/**
 * Hours calculation utilities using core data models
 */

import { TIME_CONSTANTS } from "../constants";
import { differenceInHours } from "../core/time";

/**
 * Epsilon tolerance for hours comparison (in hours)
 *
 * ARCHITECTURAL NOTE: This constant ensures consistent floating-point comparison across the codebase.
 */
export const HOURS_COMPARISON_EPSILON = 0.01;

/**
 * Check if two hour values differ significantly (beyond floating-point precision)
 *
 * This is the SINGLE SOURCE OF TRUTH for determining if hours have meaningfully changed.
 * Use this instead of strict equality or ad-hoc epsilon comparisons.
 *
 * @param {number|null|undefined} hours1 - First hours value
 * @param {number|null|undefined} hours2 - Second hours value
 * @param {number} epsilon - Tolerance for comparison (default: HOURS_COMPARISON_EPSILON)
 * @returns {boolean} True if hours differ by more than epsilon
 */
export const hoursHaveChanged = (
  hours1,
  hours2,
  epsilon = HOURS_COMPARISON_EPSILON
) => {
  const a = hours1 || 0;
  const b = hours2 || 0;
  return Math.abs(a - b) > epsilon;
};

/**
 * Calculate total shift duration (start to end, including breaks)
 */
export const calculateShiftDuration = (shift) => {
  return differenceInHours(shift.endTime, shift.startTime);
};

/**
 * Check if a shift has been properly marked as overnight
 * (end time has been adjusted to next day with >= 24 hours)
 */
export const isOvernightShift = (shift) => {
  if (shift.startTime === null || shift.endTime === null) {
    return false;
  }
  return shift.endTime >= TIME_CONSTANTS.SECONDS_PER_DAY;
};

/**
 * Check if a shift needs to be adjusted for overnight
 * (end time is before start time, indicating it should be next day)
 */
export const shouldAdjustToOvernightShift = (shift) => {
  return shift.endTime < shift.startTime;
};

/**
 * Calculate total break duration for breaks
 */
export const calculateBreaksDuration = (breaks) => {
  return breaks.reduce((total, breakData) => {
    return total + differenceInHours(breakData.endTime, breakData.startTime);
  }, 0);
};

/**
 * Calculate shift duration, returning 0 if start or end time is null
 */
export const calculateShiftDurationSafe = (shift) => {
  if (shift.startTime === null || shift.endTime === null) {
    return 0;
  }
  return calculateShiftDuration(shift);
};

/**
 * Calculate break duration, skipping breaks with null times
 */
export const calculateBreaksDurationSafe = (breaks) => {
  return breaks.reduce((total, breakData) => {
    if (breakData.startTime === null || breakData.endTime === null) {
      return total; // Skip incomplete breaks
    }
    return total + differenceInHours(breakData.endTime, breakData.startTime);
  }, 0);
};

/**
 * Calculate total break duration for a shift, including historical break hours
 *
 * @param {Object} shift - The shift object
 * @param {Object} options - Calculation options
 * @param {boolean} options.historicalFallback - When true, an empty breaks array
 *   falls back to the backend scalar `hoursBreakEmployee`. Use this for read-only
 *   summaries (e.g. the weekly breakdown), where breaks may only exist as the
 *   scalar. Leave false for live editing, where an explicit empty array means the
 *   user removed all breaks and the stale scalar must NOT be re-applied.
 */
export const calculateShiftBreakDurationSafe = (
  shift,
  { historicalFallback = false } = {}
) => {
  // Live break records always win when present.
  if (Array.isArray(shift.breaks) && shift.breaks.length > 0) {
    return calculateBreaksDurationSafe(shift.breaks);
  }
  // No live break records: use the historical scalar when there's no array at
  // all, or when the caller opts into the historical fallback for an empty array.
  if (
    (historicalFallback || !Array.isArray(shift.breaks)) &&
    shift.hoursBreakEmployee !== undefined
  ) {
    return shift.hoursBreakEmployee;
  }
  // No breaks
  return 0;
};

/**
 * Calculate net working hours with support for overrides
 *
 * ARCHITECTURAL NOTE: This is the SINGLE SOURCE OF TRUTH for hours calculation in the frontend.
 * All components should use this utility rather than implementing their own calculation logic.
 *
 * @param {Object} shift - The shift object with times, breaks, and calculated hours
 * @param {number|null} shift.startTime - Shift start (seconds since midnight)
 * @param {number|null} shift.endTime - Shift end (seconds since midnight)
 * @param {Array} shift.breaks - Array of break objects
 * @param {number|null} shift.hoursWorkedEmployee - Backend-calculated employee hours
 * @param {number|null} shift.hoursWorkedEmployer - Backend-calculated manager override hours
 * @param {boolean} shift.createdByManager - Whether shift was created by manager (not employee)
 * @param {Object} options - Calculation options
 * @param {boolean} options.accountForManagerOverrides - If true, prioritize manager override
 * @param {boolean} options.historicalBreakFallback - If true, subtract the backend
 *   scalar break (`hoursBreakEmployee`) when a shift has an empty breaks array.
 *   Use for read-only summaries so worked hours come out net, not gross.
 * @returns {number} - Net working hours
 */
export const calculateShiftNetHoursSafe = (shift, options = {}) => {
  const {
    accountForManagerOverrides = false,
    historicalBreakFallback = false,
  } = options;

  // Special case: Manager-created shifts have no employee original data
  // Always use employer hours or calculate from times
  if (shift.createdByManager) {
    // Try employer hours first
    if (shift.hoursWorkedEmployer != null) {
      return shift.hoursWorkedEmployer;
    }
    // Fall back to calculating from times
    if (shift.startTime != null && shift.endTime != null) {
      const shiftDuration = calculateShiftDuration(shift);
      const breakDuration = calculateShiftBreakDurationSafe(shift, {
        historicalFallback: historicalBreakFallback,
      });
      return Math.max(0, shiftDuration - breakDuration);
    }
    return 0;
  }

  // Priority 1: Manager override (if accounting for it and present)
  if (accountForManagerOverrides && shift.hoursWorkedEmployer != null) {
    return shift.hoursWorkedEmployer;
  }

  // Priority 2: Employee hours when manager override exists but we're not accounting for it
  // Important: If there's an override, shift.startTime/endTime are COALESCE values (employer wins),
  // so we can't recalculate employee hours from them. Use hoursWorkedEmployee directly.
  // Note: The > 0 check handles null, undefined, and zero values
  if (
    !accountForManagerOverrides &&
    shift.hoursWorkedEmployer != null &&
    shift.hoursWorkedEmployee > 0
  ) {
    return shift.hoursWorkedEmployee;
  }

  // Priority 3: Calculate from shift times (if available)
  if (shift.startTime != null && shift.endTime != null) {
    const shiftDuration = calculateShiftDuration(shift);
    const breakDuration = calculateShiftBreakDurationSafe(shift, {
      historicalFallback: historicalBreakFallback,
    });
    return Math.max(0, shiftDuration - breakDuration);
  }

  // Priority 4: Employee-side value (used as last resort)
  if (shift.hoursWorkedEmployee != null) {
    return shift.hoursWorkedEmployee;
  }

  // No data available
  return 0;
};
